import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Kate's live pre-visit checklist — derived server-side from the conversation
 * after each turn and polled by the patient-web PreVisit page (~2s). The shape is
 * shared with the frontend through `@behuman/core-contract`, so this file is the
 * single source of truth for it.
 */
export const kateChecklistSchema = z.object({
  attendance: z.enum([
    'pending',
    'confirmed',
    'reschedule_requested',
    'reschedule_confirmed',
    'cancelled',
  ]),
  /** The patient's stated reason, only with `attendance: 'cancelled'`. */
  cancelReason: z.string().optional(),
  /** The patient's own words accepting Kate's read-back; the filing verifies them against the transcript. */
  cancelConfirmationQuote: z.string().optional(),
  /** The offered time the patient accepted, only with `attendance: 'reschedule_confirmed'`. */
  rescheduleSlotStart: z.string().optional(),
  /** When they could come instead, in their own words — what the care team needs when nothing offered fits. */
  reschedulePreferences: z.string().optional(),
  visitReason: z.enum(['pending', 'captured']),
  visitReasonNote: z.string().optional(),
  insurance: z.enum(['pending', 'unchanged', 'changed']),
  videoReadiness: z.enum(['pending', 'ready', 'assistance_needed']),
  questions: z.enum(['pending', 'answered', 'none']),
  questionsNote: z.string().optional(),
  callbackRequested: z.boolean(),
  summary: z.string().optional(),
  /** Kate has said goodbye and nothing is left; the page ends the call after that sentence. */
  callComplete: z.boolean().optional(),
})
export type KateChecklist = z.infer<typeof kateChecklistSchema>

export const kateSessionStatusSchema = z.enum(['active', 'ended', 'filed', 'failed'])
export type KateSessionStatus = z.infer<typeof kateSessionStatusSchema>

/** The languages Kate can run a check-in in — chosen before Start and fixed for the call (the vendor pins it at token mint). */
export const kateLanguageSchema = z.enum(['en', 'es'])
export type KateLanguage = z.infer<typeof kateLanguageSchema>

/**
 * What the filing did with the patient's decision: carried it out, or left it
 * for the care team (a payment on record, a slot that went). Absent until filed,
 * and for a call that asked for neither.
 */
export const kateAppointmentOutcomeSchema = z.enum([
  'cancelled',
  'cancel_requested',
  'rescheduled',
  'reschedule_requested',
  'rebooking_declined',
])
export type KateAppointmentOutcome = z.infer<typeof kateAppointmentOutcomeSchema>

/**
 * The page's microphone check before Start, which runs before any vendor session
 * is minted: `heard` once the level meter moved, `silent` when the patient went on
 * without it moving, `unmeasured` when the browser cannot meter it. Logged only.
 */
export const kateMicCheckSchema = z.object({
  result: z.enum(['heard', 'silent', 'unmeasured']),
  /** From the microphone permission to the first sound (or to going on without it). */
  ms: z.number().int().min(0).max(600_000),
})

/**
 * Public-with-optional-auth (mirrors `GET /appointment/call/:encounterId`):
 * an authenticated patient proves ownership through their token; a magic-link
 * guest proves possession through `magicLink` (the same code+loginId pair the
 * link carried — an expired/used link still identifies its Login, and the
 * handler re-verifies it server-side). An anonymous create is refused: the
 * vendor token this returns is what the browser spends to start a billable
 * session, so it is never handed to an unproven caller.
 */
export const kateSessionCreate = oc
  .route({ method: 'POST', path: '/v2/kate/session' })
  .input(
    z.object({
      encounterId: z.string(),
      magicLink: z.object({ loginId: z.string(), code: z.string() }).optional(),
      /** The patient's pick on the welcome screen; the chart's preference when absent. */
      language: kateLanguageSchema.optional(),
      micCheck: kateMicCheckSchema.optional(),
    }),
  )
  .output(
    z.object({
      /** OUR signed session token — the FE's credential for status polls and the end report. */
      sessionToken: z.string(),
      /** Our session id as the server logs it, so the page's error reports can be matched to them. */
      sessionId: z.string(),
      /** LiveAvatar session token the web SDK spends to start the avatar session. */
      vendorSessionToken: z.string(),
      vendorSessionId: z.string(),
      status: kateSessionStatusSchema,
      checklist: kateChecklistSchema,
      language: kateLanguageSchema,
      appointment: z.object({
        startISO: z.string().optional(),
        providerName: z.string().optional(),
        organizationName: z.string().optional(),
      }),
      /** A missed visit's follow-up: no pre-visit checklist, and the page shows the times Kate offers. */
      rebooking: z.boolean(),
      /** The provider's free times Kate may offer, worded as she says them; empty when there are none. */
      slotOptions: z.array(z.object({ startISO: z.string(), spoken: z.string() })),
      /** Studio avatar on solid green: the page keys it out and paints its own backdrop. */
      avatar: z.object({ greenScreen: z.boolean() }),
    }),
  )

/**
 * Polled by the PreVisit page (~2s); the signed token IS the authorization.
 *
 * POST rather than GET despite being a read: oRPC serializes a GET's input into
 * the query string, which would put a six-hour credential — and the PHI it
 * unlocks — into ALB access logs on every poll for the length of every call.
 */
export const kateSessionGet = oc
  .route({ method: 'POST', path: '/v2/kate/session/status' })
  .input(z.object({ token: z.string() }))
  .output(
    z.object({
      status: kateSessionStatusSchema,
      checklist: kateChecklistSchema,
      endedReason: z.string().optional(),
      appointmentOutcome: kateAppointmentOutcomeSchema.optional(),
      /** The new visit time, once the filing has moved it. */
      rescheduledToISO: z.string().optional(),
    }),
  )

/**
 * The browser reports that the avatar session ended (`session.stopped` from the
 * SDK, or the patient leaving). LiveAvatar has no webhooks, so this is the
 * primary end signal; the `kate.file-sessions` sweep is the backstop for a
 * report that never arrives. The body is a TRIGGER only — outcomes are
 * re-derived from the vendor's transcript endpoint, never from what the browser
 * says happened.
 */
export const kateSessionEnd = oc
  .route({ method: 'POST', path: '/v2/kate/session/end' })
  .input(z.object({ token: z.string(), reason: z.string().max(64).optional() }))
  .output(z.object({ status: kateSessionStatusSchema }))

const turnMs = (max: number) => z.number().int().min(0).max(max)

/**
 * One of Kate's replies as the browser timed it, sent when her speech ends (or
 * once, as `abandoned`, when the call ends while the patient is still waiting).
 * Milliseconds from the vendor's end-of-turn (`user.speak_ended`); `endpointMs`
 * runs back from it to the last frame the microphone meter heard speech, so it
 * is approximate. A patient who spoke again before Kate answered moves that
 * end-of-turn; `firstEndMs` then runs from the turn's first one to her voice
 * (or to abandonment), the silence the patient actually sat through.
 */
export const kateTurnReportSchema = z
  .object({
    n: z.number().int().min(1).max(400),
    endpointMs: turnMs(120_000).optional(),
    textMs: turnMs(600_000).optional(),
    voiceMs: turnMs(600_000).optional(),
    firstEndMs: turnMs(600_000).optional(),
    spokeMs: turnMs(600_000).optional(),
    interrupted: z.boolean().optional(),
    abandoned: z.boolean().optional(),
    waitedMs: turnMs(600_000).optional(),
    quality: z.enum(['GOOD', 'BAD', 'UNKNOWN']).optional(),
    device: z.enum(['mobile', 'desktop']),
    network: z.string().max(16).optional(),
  })
  // A spoken reply can lack `textMs`: its text may have streamed before the
  // patient's last end of turn.
  .refine((t) => (t.abandoned ? t.waitedMs !== undefined : t.voiceMs !== undefined), {
    message: 'an abandoned turn needs waitedMs, a spoken one voiceMs',
  })
export type KateTurnReport = z.infer<typeof kateTurnReportSchema>

/**
 * One moment of a call as the browser saw it, sent as it happens so a call that
 * fails or is abandoned early still leaves its trail: the start-of-call steps,
 * Kate going silent after an answer, and why a call could not start. `atMs` runs
 * from the call view mounting (right after the session was created). Device and
 * browser come with `start` only. Logged only, never stored; no free text.
 */
const LABEL = /^[\w .-]*$/

export const kateClientEventSchema = z.object({
  name: z.enum([
    'start',
    'connected',
    'stream-ready',
    'greeting-start',
    'greeting-end',
    'first-speech',
    'greeting-late',
    'stall',
    'error',
  ]),
  atMs: turnMs(3_600_000),
  /** `error` only: what could not start. */
  error: z.enum(['mic', 'connect', 'connect-timeout']).optional(),
  /** `stall`: the silence since the patient finished; `greeting-late`: since the video was ready. */
  waitedMs: turnMs(600_000).optional(),
  device: z.enum(['mobile', 'desktop']).optional(),
  // Short labels only (they reach the log as-is): letters, digits, space, dot, dash.
  os: z.string().max(24).regex(LABEL).optional(),
  browser: z.string().max(24).regex(LABEL).optional(),
  network: z.string().max(16).regex(LABEL).optional(),
})
export type KateClientEvent = z.infer<typeof kateClientEventSchema>

/**
 * The browser's timing of one reply, for the latency log: logged only, never
 * stored. The signed token authorizes it, as for the status poll. A POST of its
 * own so the status poll stays a read.
 */
export const kateSessionTurnReport = oc
  .route({ method: 'POST', path: '/v2/kate/session/turn' })
  .input(z.object({ token: z.string(), turn: kateTurnReportSchema }))
  .output(z.object({ ok: z.literal(true) }))

/**
 * A call's start-of-call steps and failures, for the log: same token rule and
 * the same "logged, never stored" contract as the turn report.
 */
export const kateSessionEventReport = oc
  .route({ method: 'POST', path: '/v2/kate/session/event' })
  .input(z.object({ token: z.string(), event: kateClientEventSchema }))
  .output(z.object({ ok: z.literal(true) }))
